import { useEffect, useState } from "react";
import { Save, Image as ImageIcon, Megaphone, Sparkles, Star, ExternalLink, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useSiteConfig, fileUrl } from "@/lib/siteConfig";
import MediaUploader from "@/components/MediaUploader";
import { toast } from "sonner";

export default function BrandingSettings() {
  const { config, refresh } = useSiteConfig();
  const [form, setForm] = useState(config);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(config); }, [config]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings/site", form);
      await refresh();
      toast.success("Landing page updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Brand */}
      <section className="bg-white border border-line rounded-sm p-6 md:p-8" data-testid="branding-brand-section">
        <div className="flex items-center gap-2 mb-5">
          <Sparkles className="w-4 h-4 text-clay" />
          <h2 className="font-heading text-xl">Brand</h2>
        </div>
        <div className="space-y-5">
          <div>
            <Label className="text-forest">Brand name</Label>
            <Input
              value={form.brand_name || ""}
              onChange={(e) => set("brand_name", e.target.value)}
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="brand-name-input"
            />
            <p className="text-xs text-sage mt-1.5">Shown in the header next to the logo and in browser tabs.</p>
          </div>
          <div>
            <Label className="text-forest">Logo</Label>
            <p className="text-xs text-sage mb-2 mt-1">PNG with transparent background works best. SVG also supported.</p>
            <MediaUploader
              fileId={form.logo_file_id}
              onChange={(d) => set("logo_file_id", d?.id || null)}
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              label="Upload logo"
              hint="PNG, JPG, SVG, WebP up to 15MB"
              testId="logo-uploader"
            />
          </div>
          <div>
            <Label className="text-forest">Logo size</Label>
            <Select
              value={form.logo_size || "medium"}
              onValueChange={(v) => set("logo_size", v)}
            >
              <SelectTrigger className="mt-1.5 h-11 border-line" data-testid="logo-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small (32px)</SelectItem>
                <SelectItem value="medium">Medium (48px)</SelectItem>
                <SelectItem value="large">Large (64px)</SelectItem>
                <SelectItem value="xlarge">Extra large (96px)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-sage mt-1.5">
              Controls the logo height in the header and footer of the public landing page.
            </p>
          </div>
        </div>
      </section>

      {/* Contact info */}
      <section className="bg-white border border-line rounded-sm p-6 md:p-8" data-testid="branding-contact-section">
        <div className="flex items-center gap-2 mb-5">
          <Phone className="w-4 h-4 text-clay" />
          <h2 className="font-heading text-xl">Contact info</h2>
        </div>
        <p className="text-sm text-sage mb-5">
          Shown in the footer of your public landing page. Leave any field blank to hide it.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-forest">Phone number</Label>
            <Input
              value={form.contact_phone || ""}
              onChange={(e) => set("contact_phone", e.target.value)}
              placeholder="+1 (555) 010-2030"
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="contact-phone-input"
            />
          </div>
          <div>
            <Label className="text-forest">Email</Label>
            <Input
              value={form.contact_email || ""}
              onChange={(e) => set("contact_email", e.target.value)}
              placeholder="hello@yourbrand.com"
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="contact-email-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-forest">Office address</Label>
            <Input
              value={form.contact_address || ""}
              onChange={(e) => set("contact_address", e.target.value)}
              placeholder="280 Madison Ave, NY 10016"
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="contact-address-input"
            />
          </div>
        </div>
      </section>

      {/* Hero */}
      <section className="bg-white border border-line rounded-sm p-6 md:p-8" data-testid="branding-hero-section">
        <div className="flex items-center gap-2 mb-5">
          <ImageIcon className="w-4 h-4 text-clay" />
          <h2 className="font-heading text-xl">Hero section</h2>
        </div>

        <div className="space-y-5">
          <div>
            <Label className="text-forest">Hero media (image or video)</Label>
            <p className="text-xs text-sage mb-2 mt-1">
              The big visual on the right side of the homepage. Tall portrait orientation works best (4:5 ratio).
            </p>
            <MediaUploader
              fileId={form.hero_media_file_id}
              onChange={(d) => {
                set("hero_media_file_id", d?.id || null);
                if (d?.kind) set("hero_media_type", d.kind);
              }}
              accept="image/*,video/*"
              label="Upload hero image or video"
              hint="JPG, PNG, GIF, WebP (15MB) or MP4, WebM (50MB)"
              testId="hero-uploader"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div>
              <Label className="text-forest">Eyebrow tag</Label>
              <Input
                value={form.hero_eyebrow || ""}
                onChange={(e) => set("hero_eyebrow", e.target.value)}
                placeholder="Trusted by 2,400+ families"
                className="mt-1.5 h-11 border-line input-clean"
                data-testid="hero-eyebrow-input"
              />
              <p className="text-xs text-sage mt-1.5">Small clay-colored tag above the headline.</p>
            </div>
          </div>

          <div className="border border-line bg-cream/40 rounded-sm p-5">
            <Label className="text-forest mb-2 block">Headline (3 parts)</Label>
            <p className="text-xs text-sage mb-4">
              The middle part is highlighted in clay color. Together they read as one sentence.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-sage">Prefix</Label>
                <Input
                  value={form.hero_title_prefix || ""}
                  onChange={(e) => set("hero_title_prefix", e.target.value)}
                  className="mt-1 h-10 border-line input-clean"
                  data-testid="hero-prefix-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-clay">Highlighted</Label>
                <Input
                  value={form.hero_title_highlight || ""}
                  onChange={(e) => set("hero_title_highlight", e.target.value)}
                  className="mt-1 h-10 border-line input-clean text-clay"
                  data-testid="hero-highlight-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-sage">Suffix</Label>
                <Input
                  value={form.hero_title_suffix || ""}
                  onChange={(e) => set("hero_title_suffix", e.target.value)}
                  className="mt-1 h-10 border-line input-clean"
                  data-testid="hero-suffix-input"
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-forest">Subtitle</Label>
            <Textarea
              value={form.hero_subtitle || ""}
              onChange={(e) => set("hero_subtitle", e.target.value)}
              rows={3}
              className="mt-1.5 border-line input-clean resize-none"
              data-testid="hero-subtitle-input"
            />
          </div>

          <div className="bg-cream/40 border border-line rounded-sm p-4 space-y-3">
            <div className="text-xs uppercase tracking-[0.15em] text-clay font-semibold">
              Response card (under the CTAs)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-forest text-sm">Small label</Label>
                <Input
                  value={form.response_label || ""}
                  onChange={(e) => set("response_label", e.target.value)}
                  placeholder="Avg. response"
                  className="mt-1 h-10 border-line input-clean bg-white"
                  data-testid="response-label-input"
                />
              </div>
              <div>
                <Label className="text-forest text-sm">Big value</Label>
                <Input
                  value={form.response_value || ""}
                  onChange={(e) => set("response_value", e.target.value)}
                  placeholder="2.4 hrs"
                  className="mt-1 h-10 border-line input-clean bg-white"
                  data-testid="response-value-input"
                />
              </div>
              <div>
                <Label className="text-forest text-sm">Right-side text</Label>
                <Input
                  value={form.response_suffix || ""}
                  onChange={(e) => set("response_suffix", e.target.value)}
                  placeholder="to first agent contact"
                  className="mt-1 h-10 border-line input-clean bg-white"
                  data-testid="response-suffix-input"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-forest">Health CTA button</Label>
              <Input
                value={form.cta_health_label || ""}
                onChange={(e) => set("cta_health_label", e.target.value)}
                className="mt-1.5 h-11 border-line input-clean"
                data-testid="cta-health-input"
              />
            </div>
            <div>
              <Label className="text-forest">Life CTA button</Label>
              <Input
                value={form.cta_life_label || ""}
                onChange={(e) => set("cta_life_label", e.target.value)}
                className="mt-1.5 h-11 border-line input-clean"
                data-testid="cta-life-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Promo banner */}
      <section className="bg-white border border-line rounded-sm p-6 md:p-8" data-testid="branding-promo-section">
        <div className="flex items-center gap-2 mb-5">
          <Megaphone className="w-4 h-4 text-clay" />
          <h2 className="font-heading text-xl">Promo banner</h2>
        </div>

        <div className="flex items-center justify-between pb-5 border-b border-line">
          <div>
            <Label className="text-forest font-medium">Show promo banner</Label>
            <p className="text-sm text-sage mt-1">A thin clay-colored bar at the top of the landing page.</p>
          </div>
          <Switch
            checked={!!form.promo_enabled}
            onCheckedChange={(v) => set("promo_enabled", v)}
            data-testid="promo-enabled-switch"
          />
        </div>

        <div className="space-y-4 pt-5">
          <div>
            <Label className="text-forest">Message</Label>
            <Input
              value={form.promo_message || ""}
              onChange={(e) => set("promo_message", e.target.value)}
              placeholder="e.g. Special: 20% off life insurance through March"
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="promo-message-input"
            />
          </div>
          <div>
            <Label className="text-forest">Optional link <span className="text-sage font-normal">(where the banner sends visitors)</span></Label>
            <Input
              value={form.promo_url || ""}
              onChange={(e) => set("promo_url", e.target.value)}
              placeholder="https://… or /apply/health"
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="promo-url-input"
            />
          </div>
        </div>

        {form.promo_enabled && form.promo_message && (
          <div className="mt-5 bg-clay text-bone text-sm text-center py-2.5 px-4 rounded-sm" data-testid="promo-preview">
            Preview → {form.promo_message}
          </div>
        )}
      </section>

      {/* Product cards */}
      <section className="bg-white border border-line rounded-sm p-6 md:p-8" data-testid="branding-products-section">
        <div className="flex items-center gap-2 mb-5">
          <Star className="w-4 h-4 text-clay" />
          <h2 className="font-heading text-xl">Product cards</h2>
        </div>
        <p className="text-sm text-sage mb-5">
          The two cards in the "Two ways to start" section on the landing page.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-line rounded-sm p-4 bg-cream/30">
            <div className="text-xs uppercase tracking-[0.15em] text-clay font-semibold mb-3">Health card</div>
            <Label className="text-forest text-sm">Title</Label>
            <Input
              value={form.product_health_title || ""}
              onChange={(e) => set("product_health_title", e.target.value)}
              className="mt-1 h-10 border-line input-clean bg-white"
              data-testid="product-health-title-input"
            />
            <Label className="text-forest text-sm mt-3 block">Description</Label>
            <Textarea
              value={form.product_health_desc || ""}
              onChange={(e) => set("product_health_desc", e.target.value)}
              rows={2}
              className="mt-1 border-line input-clean bg-white resize-none"
              data-testid="product-health-desc-input"
            />
          </div>
          <div className="border border-line rounded-sm p-4 bg-cream/30">
            <div className="text-xs uppercase tracking-[0.15em] text-clay font-semibold mb-3">Life card</div>
            <Label className="text-forest text-sm">Title</Label>
            <Input
              value={form.product_life_title || ""}
              onChange={(e) => set("product_life_title", e.target.value)}
              className="mt-1 h-10 border-line input-clean bg-white"
              data-testid="product-life-title-input"
            />
            <Label className="text-forest text-sm mt-3 block">Description</Label>
            <Textarea
              value={form.product_life_desc || ""}
              onChange={(e) => set("product_life_desc", e.target.value)}
              rows={2}
              className="mt-1 border-line input-clean bg-white resize-none"
              data-testid="product-life-desc-input"
            />
          </div>
        </div>
      </section>

      {/* Why InsureWell editor */}
      <section className="bg-white border border-line rounded-sm p-6 md:p-8" data-testid="branding-why-section">
        <div className="flex items-center gap-2 mb-5">
          <Star className="w-4 h-4 text-clay" />
          <h2 className="font-heading text-xl">"Why us" section</h2>
        </div>
        <p className="text-sm text-sage mb-5">
          The 4-card section on your landing page. Icons stay fixed (lock, document, people, heart) but you can edit
          the eyebrow, section title, and each card's title + description.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <div>
            <Label className="text-forest">Eyebrow</Label>
            <Input
              value={form.why_eyebrow || ""}
              onChange={(e) => set("why_eyebrow", e.target.value)}
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="why-eyebrow-input"
            />
          </div>
          <div>
            <Label className="text-forest">Section title</Label>
            <Input
              value={form.why_title || ""}
              onChange={(e) => set("why_title", e.target.value)}
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="why-title-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="border border-line rounded-sm p-4 bg-cream/30">
              <div className="text-xs uppercase tracking-[0.15em] text-clay font-semibold mb-3">Card {n}</div>
              <Label className="text-forest text-sm">Title</Label>
              <Input
                value={form[`why_box_${n}_title`] || ""}
                onChange={(e) => set(`why_box_${n}_title`, e.target.value)}
                className="mt-1 h-10 border-line input-clean bg-white"
                data-testid={`why-box-${n}-title-input`}
              />
              <Label className="text-forest text-sm mt-3 block">Description</Label>
              <Textarea
                value={form[`why_box_${n}_desc`] || ""}
                onChange={(e) => set(`why_box_${n}_desc`, e.target.value)}
                rows={2}
                className="mt-1 border-line input-clean bg-white resize-none"
                data-testid={`why-box-${n}-desc-input`}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Apply / Agent URL */}
      <section className="bg-white border border-line rounded-sm p-6 md:p-8" data-testid="branding-apply-url-section">
        <div className="flex items-center gap-2 mb-5">
          <ExternalLink className="w-4 h-4 text-clay" />
          <h2 className="font-heading text-xl">Apply &amp; Agent links destination</h2>
        </div>
        <div>
          <Label className="text-forest">Send Apply / Agent buttons to</Label>
          <Input
            value={form.apply_base_url || ""}
            onChange={(e) => set("apply_base_url", e.target.value)}
            placeholder="e.g. https://apply.insurewell.health   (leave empty to keep on the same site)"
            className="mt-1.5 h-11 border-line input-clean"
            data-testid="apply-base-url-input"
          />
          <p className="text-xs text-sage mt-2 leading-relaxed">
            Use this to host your <span className="font-medium text-forest">marketing landing page</span> on one
            domain (e.g. <code className="bg-cream px-1 rounded">insurewell.health</code>) and your <span className="font-medium text-forest">application portal</span> on another
            (e.g. <code className="bg-cream px-1 rounded">apply.insurewell.health</code>). When set, every "Apply for…" / "Agent Login"
            button on this landing page will jump visitors to that domain. Leave empty if both live on the same site.
          </p>
        </div>
      </section>

      {/* Save */}
      <div className="flex items-center justify-end gap-3 sticky bottom-4">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-forest hover:bg-forestdark text-bone rounded-sm h-11 px-6 shadow-md"
          data-testid="branding-save-btn"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
